import React, { useEffect, useRef, useState } from 'react'
import { Button, CircularProgress, makeStyles, Tooltip, Typography } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import pollingSlice, {
  getAnswersOfSpecificPoll,
  getPollById,
  getQuestionsByPollId,
  setPollQuestionActiveOrInActive,
  updatePollStatus,
  getPollAssociatedDataAlongSpeaker,
  updatePollById
} from 'store/polling'
import { Box } from '@mui/material'
import { ChevronLeft, ChevronRight, PlayArrow, Stop, Pause } from '@material-ui/icons'
import PollQuestion from 'components/PollQuestion'
import { PollStatus } from 'constants/index'
import { colors } from 'utils/colors'
import { useParams } from 'react-router-dom'
import PresentationMode from './PresentationMode'
import ProgressButton from 'components/ProgressButton'

const PollView = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { pollId } = useParams()

  const fullScreenRef = useRef(null)

  const [isPollStarted, setIsPollStarted] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isMovingQuestion, setIsMovingQuestion] = useState(false)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [presentationMode, setPresentationMode] = useState(false)

  const { inProgress, poll, pollAssociatedData, questions } = useSelector((state) => state.polling)
  const handleUnload = () => {
    const currentQuestionId = questions[questionIndex]?.id
    if (poll?.id && poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED) {
      navigator.sendBeacon(
        process.env.REACT_APP_FIREBASE_END_POLL_FUNCTION_URL,
        JSON.stringify({ pollId: pollId, questionId: currentQuestionId })
      )
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [poll?.id, poll?.status])

  const handleOpenFullScreen = () => {
    if (!presentationMode) {
      setPresentationMode(true)
      if (fullScreenRef.current.requestFullscreen) {
        fullScreenRef.current.requestFullscreen()
      } else if (fullScreenRef.current.mozRequestFullScreen) {
        fullScreenRef.current.mozRequestFullScreen()
      } else if (fullScreenRef.current.webkitRequestFullscreen) {
        fullScreenRef.current.webkitRequestFullscreen()
      } else if (fullScreenRef.current.msRequestFullscreen) {
        fullScreenRef.current.msRequestFullscreen()
      }
    }
  }

  const handleCloseFullScreen = () => {
    if (presentationMode) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      setPresentationMode(false)
    }
  }
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setPresentationMode(false)
      handleCloseFullScreen()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(true)
  }

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }

  const handleConfirmModal = async () => {
    setIsUpdating(true)
    const currentQuestionId = questions[questionIndex]?.id
    await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
    await dispatch(updatePollById(poll?.id, { status: PollStatus.CLOSED, isActive: false }))
    handleCloseConfirmModal()
    setIsUpdating(false)
    handleCloseFullScreen()
    window.close()
  }

  const activeQuestionIndex =
    poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED
      ? questions?.findIndex((question) => question?.isActive === true)
      : -1

  useEffect(() => {
    if (activeQuestionIndex > -1) {
      setQuestionIndex(activeQuestionIndex)
    }
  }, [activeQuestionIndex])

  useEffect(() => {
    const fetchAssociatedData = async () => {
      setIsFetching(true)
      await dispatch(
        getPollAssociatedDataAlongSpeaker(
          poll?.talkId,
          poll?.sessionId,
          poll?.eventId,
          poll?.speakerId
        )
      )
      setIsFetching(false)
    }

    fetchAssociatedData()
  }, [poll?.id])

  const updatePoll = async (status) => {
    setIsUpdating(true)
    await dispatch(updatePollStatus(poll?.id, status))
    setIsUpdating(false)
  }

  const handleQuestionBack = async () => {
    setIsMovingQuestion(true)
    if (poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED) {
      const currentQuestionId = questions[questionIndex]?.id
      const prevQuestionId = questions[questionIndex - 1]?.id
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, prevQuestionId, true))
    }
    setIsMovingQuestion(false)
    setQuestionIndex(questionIndex - 1)
  }

  const handleQuestionNext = async () => {
    setIsMovingQuestion(true)
    if (poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED) {
      const currentQuestionId = questions[questionIndex]?.id
      const nextQuestionId = questions[questionIndex + 1]?.id
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, nextQuestionId, true))
    }
    setIsMovingQuestion(false)
    setQuestionIndex(questionIndex + 1)
  }

  useEffect(() => {
    if (pollId) {
      dispatch(pollingSlice.actions.setProgress(true))
      const unsubPoll = getPollById(pollId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      const unsub = getQuestionsByPollId(pollId, (data) => {
        dispatch(pollingSlice.actions.setQuestions(data))
      })
      const unsubscribeAnswers = getAnswersOfSpecificPoll(pollId, (data) => {
        dispatch(pollingSlice.actions.setAnswers(data))
      })
      return () => {
        unsubPoll && unsubPoll()
        unsub && unsub()
        unsubscribeAnswers && unsubscribeAnswers()
      }
    }
  }, [dispatch, pollId])

  const handleStartPoll = async () => {
    handleOpenFullScreen()
    setIsUpdating(true)
    const currentQuestionId = questions[questionIndex]?.id
    await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, true))
    const payload = {
      isActive: true,
      status: PollStatus.STARTED
    }
    await dispatch(updatePollById(pollId, payload))
    setIsPollStarted(true)
    setIsUpdating(false)
  }

  if (inProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10rem' }}>
        <CircularProgress />
      </Box>
    )
  } else if (!poll?.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10rem' }}>
        <Box sx={{ width: '50%' }}>
          <Typography variant="h4" style={{ textAlign: 'center', lineHeight: '1.5' }}>
            It looks like the poll you&apos;re looking for doesn&apos;t exist. Please verify the URL
            and try again.
          </Typography>
        </Box>
      </Box>
    )
  } else if (!presentationMode && poll?.status === PollStatus.CLOSED) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10rem' }}>
        <Box sx={{ width: '50%' }}>
          <Typography variant="h4" style={{ textAlign: 'center', lineHeight: '1.5' }}>
            This poll has ended. Please verify the URL and try again.
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      ref={fullScreenRef}
      sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PresentationMode>
        {poll?.status === PollStatus.CREATED && !isPollStarted && (
          <Box
            className={classes.backdrop}
            style={{ backdropFilter: 'blur(2px)', zIndex: '99999' }}>
            <Box className={classes.backdropContent}>
              <div className={classes.card}>
                {isFetching ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '1rem',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4rem',
                      margin: '1rem 4rem'
                    }}>
                    <CircularProgress size={35} /> Loading...
                  </Box>
                ) : (
                  <div>
                    <div className={classes.cardContent}>
                      <div className={classes.row}>
                        <p className={classes.key}>Poll Title:</p>
                        <p className={classes.value}>{poll?.title ?? 'N/A'}</p>
                      </div>
                      <div className={classes.row}>
                        <p className={classes.key}>{pollAssociatedData?.type}:</p>
                        <p className={classes.value}>{pollAssociatedData?.title ?? 'N/A'}</p>
                      </div>
                      <div className={classes.row}>
                        <p className={classes.key}>Presenter:</p>
                        <p className={classes.value}>{pollAssociatedData?.presenter ?? 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  className={isUpdating || isFetching ? classes.disabledStart : classes.start}
                  onClick={handleStartPoll}>
                  {isUpdating ? (
                    'Starting...'
                  ) : (
                    <>
                      <PlayArrow fontSize="large" className={classes.startIcon} />
                      Start Poll
                    </>
                  )}
                </div>
              </Box>
            </Box>
          </Box>
        )}
        <PollQuestion question={questions[questionIndex]} />
        {openConfirmModal && (
          <Box
            className={classes.backdrop}
            style={{ backdropFilter: 'blur(2px)', zIndex: '99999' }}>
            <Box className={classes.backdropContent}>
              <div className={classes.warningCard}>
                <p style={{ fontSize: '18px', fontWeight: 'bold' }}>End Poll</p>
                <p style={{ fontSize: '16px', marginRight: '4rem' }}>
                  Do you really want to end this poll?
                </p>
                <Box className={classes.btnBox}>
                  <Button color="inherit" onClick={handleCloseConfirmModal} disabled={isUpdating}>
                    No
                  </Button>
                  <ProgressButton
                    label="yes"
                    inProgress={isUpdating}
                    disabled={isUpdating}
                    onClick={handleConfirmModal}
                  />
                </Box>
              </div>
            </Box>
          </Box>
        )}
      </PresentationMode>
      {poll?.status !== PollStatus.CREATED && (
        <Box className={classes.bottomBarWrapper}>
          <Box className={classes.bottomBar}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Tooltip title={<Typography>Previous</Typography>}>
                <Button
                  variant="outlined"
                  disabled={!questionIndex > 0 || isMovingQuestion}
                  onClick={handleQuestionBack}>
                  <ChevronLeft fontSize="large" className={classes.arrow} />
                </Button>
              </Tooltip>
              <Typography className={classes.questionIndex}>
                {questionIndex + 1}/{questions.length}
              </Typography>
              <Tooltip title={<Typography>Next</Typography>}>
                <Button
                  variant="outlined"
                  disabled={questionIndex === questions?.length - 1 || isMovingQuestion}
                  onClick={handleQuestionNext}>
                  <ChevronRight fontSize="large" className={classes.arrow} />
                </Button>
              </Tooltip>
            </Box>
            <Box className={classes.actionBtns}>
              <Button
                size="large"
                startIcon={poll?.status === PollStatus.PAUSED ? <PlayArrow /> : <Pause />}
                variant="contained"
                color={poll?.status === PollStatus.PAUSED ? 'primary' : 'inherit'}
                className={poll?.status === PollStatus.PAUSED ? classes.startBtn : classes.pauseBtn}
                disabled={
                  isUpdating ||
                  poll?.status === PollStatus.CREATED ||
                  poll?.status === PollStatus.CLOSED
                }
                onClick={() =>
                  updatePoll(
                    poll?.status === PollStatus.PAUSED ? PollStatus.RESUMED : PollStatus.PAUSED
                  )
                }>
                {poll?.status === PollStatus.PAUSED ? 'Resume Poll' : 'Pause Poll'}
              </Button>
              <Button
                size="large"
                className={classes.errorBtn}
                startIcon={<Stop />}
                color="error"
                variant="contained"
                disabled={poll?.status === PollStatus.CLOSED}
                onClick={handleOpenConfirmModal}>
                End Poll
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default PollView

const commonStart = {
  color: '#FFFFFF',
  textTransform: 'none',
  width: '240px',
  fontSize: '26px',
  fontWeight: '600',
  letterSpacing: '1.25px',
  borderRadius: '12px',
  borderColor: colors.indigo,
  display: 'flex',
  justifyContent: 'center',
  gap: '4px',
  alignItems: 'center',
  padding: '16px 20px',
  cursor: 'pointer'
}

const useStyles = makeStyles({
  bottomBarWrapper: {
    position: 'absolute',
    bottom: '26px',
    height: '98px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0px'
  },
  bottomBar: {
    backgroundColor: '#eeeeee',
    padding: '16px 20px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    margin: '0px auto'
  },
  start: {
    ...commonStart,
    backgroundColor: colors.indigo,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#007E99'
    }
  },
  arrow: {
    fontSize: '50px !important'
  },
  card: {
    width: '100%',
    border: '2px solid #c2c2c2',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: 'inset 0 0 0 1px #c2c2c2'
  },
  warningCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    border: '1px solid white',
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF'
  },
  cardContent: {
    margin: '1.5rem 1.5rem 3rem 1.5rem'
  },
  boldGuide: {
    fontSize: '32px',
    fontWeight: 'bold'
  },
  cancel: {
    ...commonStart,
    width: '200px',
    color: '#757575',
    backgroundColor: '#eeeeee',
    '&:hover': {
      backgroundColor: '#bdbdbd'
    }
  },
  disabledStart: {
    ...commonStart,
    pointerEvents: 'none',
    backgroundColor: '#E0E0E0',
    color: '#BDBDBD'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '12px'
  },
  key: {
    color: '#181818',
    fontSize: '24px',
    fontWeight: 'bold',
    minWidth: '140px',
    letterSpacing: '0.75px'
  },
  value: {
    color: '#2f2f2f',
    fontSize: '24px'
  },
  startIcon: {
    fontSize: '35px !important'
  },
  backdropContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    minWidth: '560px',
    maxWidth: '800px'
  },
  bold: {
    fontWeight: 'bold'
  },
  actionBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  startBtn: {
    fontSize: '22px',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '8px 20px'
  },
  pauseBtn: {
    fontSize: '22px',
    backgroundColor: '#f3ad09',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '8px 20px',
    '&:hover': {
      backgroundColor: '#ff9a00'
    }
  },
  errorBtn: {
    fontSize: '22px',
    backgroundColor: '#d32f2f',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '8px 20px',
    '&:hover': {
      backgroundColor: '#c62828'
    }
  },
  questionIndex: {
    fontSize: '22px',
    letterSpacing: '1px',
    color: 'black'
  },
  exitBtn: {
    textTransform: 'none'
  },
  backdrop: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0
  },
  modalTitle: {
    fontSize: '17px'
  },
  modalText: {
    marginTop: '16px',
    fontSize: '14px'
  },
  modalActions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'end',
    gap: '4px',
    marginBottom: '4px'
  },
  btnBox: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'right',
    gap: '0.5rem'
  },
  yesBtn: {
    color: 'white'
  }
})
